import { Box, FormHelperText, Grid, IconButton, InputLabel, Typography } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import CloseIcon from "@material-ui/icons/Close"
import ImageIcon from "@material-ui/icons/Image"
import UploadDropZone from "@rpldy/upload-drop-zone"
import Uploady, { UploadyContext, useItemFinishListener } from "@rpldy/uploady"
import React, { useCallback, useContext } from "react"

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1.5),
    border: "1px solid " + theme.palette.grey["400"],
    borderRadius: theme.shape.borderRadius,
  },
  containerInline: {
    padding: theme.spacing(1.5),
    border: "1px solid #d7dbe0",
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
  },
  inlineLabel: {
    color: "#98a1ad",
    fontSize: "1rem",
    lineHeight: 1.2,
  },
  inlinePreviewWrapper: {
    position: "relative",
    padding: theme.spacing(0.6),
    borderRadius: 10,
    border: "1px dashed #d7dbe0",
    backgroundColor: "#eef0f2",
  },
  cursorPointer: {
    cursor: "pointer",
  },
  imgPreview: {
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.grey["400"],
    border: "none",
    "& > img": {
      maxWidth: "100%",
      maxHeight: "100%",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translateX(-50%) translateY(-50%)",
      objectFit: "contain",
    },
    "&.withBorders": {
      border: "1px dashed " + theme.palette.grey["400"],
    },
  },
  inlinePreview: {
    backgroundColor: "#96a3b2",
    color: "#d8dde3",
    border: "none",
  },
  fileName: {
    flexGrow: 1,
    color: "inherit",
    "&.grey": {
      color: theme.palette.grey["600"],
    },
  },
  inlineDelete: {
    position: "absolute",
    top: -8,
    right: -8,
    backgroundColor: "#6f7882",
    color: "#fff",
    padding: 4,
    "&:hover": {
      backgroundColor: "#59616b",
    },
  },
}))

const UploadPreviewer = ({ name, value, onChange, imgWidth = 150, helperText, showFilename = false, layout = "default" }) => {
  const classes = useStyles()
  const uploady = useContext(UploadyContext)
  const [url, setUrl] = React.useState(value)

  useItemFinishListener((item) => {
    setUrl(item.uploadResponse.data)
  })

  const onClick = useCallback(() => {
    uploady.showFileUpload()
  }, [uploady])

  const onDelete = (event) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setUrl("")
  }

  React.useEffect(() => {
    if (onChange) onChange(url)
  }, [url])

  const imgUrl = url + `?width=${imgWidth}&height=${imgWidth}`
  const isInline = layout === "inline"

  return (
    <>
      {!isInline && name && (
        <Box mb={0.25}>
          <InputLabel shrink>{name}</InputLabel>
        </Box>
      )}

      <Box className={isInline ? classes.containerInline : classes.container}>
        <Grid container alignItems='center' spacing={1}>
          {isInline && (
            <Grid item xs>
              <Typography className={classes.inlineLabel}>{name}</Typography>
            </Grid>
          )}

          <Grid item xs='auto' onClick={onClick} className={classes.cursorPointer}>
            <Box className={isInline ? classes.inlinePreviewWrapper : ""}>
              <Box
                className={classes.imgPreview + " " + (url ? "" : "withBorders") + " " + (isInline ? classes.inlinePreview : "")}
                style={{ width: imgWidth, height: imgWidth }}
              >
                {url ? <img src={imgUrl} alt='' /> : <ImageIcon />}
              </Box>

              {isInline && url && (
                <IconButton size='small' aria-label='delete' onClick={(event) => onDelete(event)} className={classes.inlineDelete}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              )}
            </Box>
          </Grid>

          {!isInline && (
            <Grid
              item
              xs
              className={classes.fileName + " " + (showFilename ? classes.cursorPointer : "") + " " + (url ? "" : "grey")}
              onClick={(e) => (showFilename ? onClick(e) : null)}
            >
              {showFilename && <Typography variant='body2'>{url ? /[^/]*$/.exec(url)[0] : "DÃ©posez votre logo ici"}</Typography>}
            </Grid>
          )}

          {!isInline && url && (
            <Grid item xs='auto'>
              <Box textAlign='right'>
                <IconButton size='small' aria-label='delete' onClick={(event) => onDelete(event)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {helperText && (
        <Box ml={1}>
          <FormHelperText>{helperText}</FormHelperText>
        </Box>
      )}
    </>
  )
}

export default function UploadFile({ name, value, uploadUrl, onChange, previewWidth, helperText, showFilename = true, layout = "default" }) {
  const finalUploadUrl = uploadUrl ?? process.env.REACT_APP_PUBLIC_MEDIA_URL

  return (
    <Uploady destination={{ url: finalUploadUrl }}>
      <UploadDropZone>
        <Box mt={-1}>
          <UploadPreviewer
            layout={layout}
            showFilename={showFilename}
            name={name}
            value={value}
            onChange={onChange}
            imgWidth={previewWidth}
            helperText={helperText}
          />
        </Box>
      </UploadDropZone>
    </Uploady>
  )
}
